// English translations
const en = {
  // Common
  common: {
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    search: "Search",
    filter: "Filter",
    export: "Export",
    back: "Back",
    next: "Next",
    confirm: "Confirm",
    close: "Close",
    yes: "Yes",
    no: "No",
    all: "All",
    none: "None",
    actions: "Actions",
    status: "Status",
    date: "Date",
    amount: "Amount",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    currency: "Currency",
    description: "Description",
    details: "Details",
    settings: "Settings",
    logout: "Logout",
    login: "Login",
    register: "Register",
    email: "Email",
    password: "Password",
    username: "Name or Username",
    name: "Name",
    phone: "Phone",
    address: "Address",
    language: "Language",
    selectLanguage: "Select language",
    backup: "Backup",
    support: "Support",
  },

  // Landing Page
  landing: {
    heroTitle: "Accept Payments",
    heroTitleHighlight: "Anywhere",
    heroSubtitle: "Create payment links, automatic invoices and integrate APIs in minutes. All with your custom branding.",
    getStarted: "Get Started Free",
    startFree: "Start Free",
    accessDashboard: "Access Dashboard",
    smartPaymentGateway: "Smart Payment Gateway",
    features: {
      title: "Everything you need to accept payments",
      subtitle: "A complete platform to manage your online payments, with advanced features and guaranteed security.",
      learnMore: "Learn more",
      paymentLinks: {
        title: "Payment Links",
        description: "Create customized payment links in seconds. QR code included."
      },
      invoicing: {
        title: "Integrated Invoicing",
        description: "Generate professional invoices with automatic payment links."
      },
      api: {
        title: "Powerful APIs",
        description: "Integrate payments into your applications with our REST APIs."
      },
      security: {
        title: "Total Security",
        description: "End-to-end encryption and security standard compliance."
      },
      agents: {
        title: "Agent Network",
        description: "Build your partner network and earn commissions."
      },
      analytics: {
        title: "Advanced Analytics",
        description: "Monitor your transactions with real-time dashboards."
      }
    },
    benefits: {
      title: "Why choose Hola Pay?",
      subtitle: "We are the ideal solution for merchants who want to accept payments simply, securely and professionally.",
      noSetupFee: "No setup fee",
      transparentFees: "Transparent fees",
      multiCurrency: "Multi-currency support",
      whiteLabel: "Complete white-label",
      customDashboard: "Customizable dashboard",
      realTimeWebhook: "Real-time webhooks"
    },
    cta: {
      title: "Ready to start?",
      subtitle: "Create your free account and start accepting payments in minutes.",
      button: "Create Free Account"
    },
    pos: {
      badge: "Coming Soon",
      title: "POS Terminals",
      titleHighlight: "Hola Pay",
      subtitle: "Accept payments anywhere with our next-generation terminals. Contactless, chip & PIN, pre-authorizations for hotels and car rentals.",
      feature1: "Transactions in less than 2 seconds",
      feature2: "Maximum security with end-to-end encryption",
      feature3: "Accept all international cards",
      feature4: "Unified dashboard with all your earnings",
      cta: "Register to be notified"
    },
    footer: {
      rights: "All rights reserved."
    }
  },

  // Auth
  auth: {
    welcomeBack: "Welcome Back",
    loginSubtitle: "Sign in to your account to manage payments",
    createAccount: "Create your account",
    registerSubtitle: "Start accepting payments in minutes",
    businessName: "Business Name",
    usernameOptional: "Name (optional)",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    loginButton: "Sign In",
    registerButton: "Create Account",
    loggingIn: "Signing in...",
    registering: "Creating account...",
    loginSuccess: "Successfully signed in!",
    registerSuccess: "Account created successfully!",
    invalidCredentials: "Invalid credentials",
    serviceUnavailable: "Service temporarily unavailable",
    registrationError: "Registration error",
    registrationSuspendedTitle: "Registrations temporarily suspended",
    registrationSuspendedMessage: "New merchant account creation is temporarily suspended. Please try again later or contact support.",
    contactSupport: "Contact support",
    close: "Close",
    registrationStatusLoading: "Checking registration availability…",
    registrationStatusError: "Registration status could not be checked. The form is disabled for your safety.",
    registrationStatusRetry: "Try again",
    registrationSuspendedDetails: "Show details",
    backToHome: "Back to Home",
    backToLogin: "Back to Login",
    demoCredentials: "Demo Admin:",
    // Forgot Password
    forgotPasswordTitle: "Forgot Password?",
    forgotPasswordSubtitle: "Enter your email and we'll send you a link to reset your password.",
    sendResetLink: "Send Reset Link",
    sending: "Sending...",
    emailSent: "Email Sent",
    emailSentMessage: "If the email address is associated with an account, you will receive a link to reset your password.",
    // Reset Password
    resetPasswordTitle: "Reset Password",
    resetPasswordSubtitle: "Enter your new password.",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    resetButton: "Reset Password",
    resetting: "Resetting...",
    passwordReset: "Password Reset!",
    passwordResetSuccess: "Your password has been successfully reset. You can now sign in with your new password.",
    goToLogin: "Go to Login",
    invalidLink: "Invalid Link",
    invalidLinkMessage: "The password reset link is invalid. Please request a new link.",
    expiredLink: "Link Expired",
    expiredLinkMessage: "The password reset link has expired or is invalid. Please request a new link.",
    requestNewLink: "Request New Link",
    passwordMinLength: "Minimum 8 characters",
    passwordsNotMatch: "Passwords do not match",
    passwordTooShort: "Password must be at least 8 characters",
    // Change Password
    changePasswordTitle: "Change Password",
    changePasswordSubtitle: "Enter your current password and new password.",
    currentPassword: "Current Password",
    changeButton: "Change Password",
    changing: "Changing...",
    passwordChanged: "Password changed successfully!",
    changePasswordError: "Error changing password",
    mandatoryChange: "Mandatory Password Change",
    mandatoryChangeMessage: "For security reasons, you must change your password on first login.",
    registerBenefits: {
      title: "Start receiving payments now",
      noActivation: "No activation fee",
      quickSetup: "Setup in less than 5 minutes",
      dashboardIncluded: "Complete dashboard included"
    },
    loginBenefits: {
      title: "Manage your payments with ease",
      subtitle: "Complete dashboard to monitor transactions, create payment links and manage invoices."
    }
  },

  // Dashboard
  dashboard: {
    overview: "Overview",
    paymentLinks: "Payment Links",
    subscriptions: "Subscriptions",
    invoices: "Invoices",
    transactions: "Transactions",
    emailTransactionSearch: "Search by email",
    apiKeys: "API Keys",
    branding: "Branding",
    withdrawals: "Withdrawals",
    settings: "Settings",
    merchants: "Merchants",
    agents: "Agents",
    gateways: "Gateways",
    auditLog: "Audit Log",
    terminals: "Terminals",
    merchantNetwork: "Merchant Network",
  },

  // Merchant Dashboard
  merchant: {
    availableBalance: "Available Balance",
    pendingBalance: "Pending Balance",
    totalCollected: "Total Collected",
    commissionsPaid: "Commissions Paid",
    quickActions: "Quick Actions",
    newPaymentLink: "New Payment Link",
    newInvoice: "New Invoice",
    requestWithdrawal: "Request Withdrawal",
    transactionsChart: "Transactions Chart",
    completed: "Completed",
    failed: "Failed",
    refunded: "Refunded",
    welcomeDashboard: "Welcome to your Dashboard",
    managePaymentsIn: "Manage your payments in",
    withdraw: "Withdraw",
    createPaymentLinkDesc: "Create a link to receive payments",
    createInvoiceDesc: "Generate a professional invoice",
    transferFundsDesc: "Transfer your funds",
    last7Days: "Last 7 days",
  },

  // Payment Links
  paymentLinks: {
    title: "Payment Links",
    subtitle: "Create and manage your payment links",
    createNew: "New Link",
    createTitle: "Create Payment Link",
    description: "Description",
    descriptionPlaceholder: "E.g.: Consulting payment",
    amount: "Amount (empty = open)",
    currency: "Currency",
    expiration: "Expiration",
    createButton: "Create Link",
    creating: "Creating...",
    copyLink: "Copy link",
    showQR: "Show QR",
    openLink: "Open link",
    deleteLink: "Delete",
    linkCopied: "Link copied!",
    linkCreated: "Payment link created!",
    linkDeleted: "Link deleted",
    noLinks: "No links created",
    noLinksSubtitle: "Create your first payment link to start receiving payments",
    createFirst: "Create first link",
    status: {
      active: "Active",
      paid: "Paid",
      expired: "Expired"
    },
    openAmount: "Open amount",
    created: "Created",
    expires: "Expires",
    qrCode: "QR Code"
  },

  // Invoices
  invoices: {
    title: "Invoices",
    subtitle: "Create and manage your invoices with payment links",
    createNew: "New Invoice",
    createTitle: "Create New Invoice",
    customerData: "Customer Data",
    customerName: "Customer Name",
    customerEmail: "Customer Email",
    customerAddress: "Address",
    customerVat: "VAT Number",
    invoiceItems: "Invoice Items",
    addItem: "Add",
    item: "Item",
    itemDescription: "Service description",
    quantity: "Quantity",
    unitPrice: "Unit Price",
    taxRate: "Tax %",
    discountAmount: "Discount",
    dueDate: "Due Date",
    notes: "Notes",
    notesPlaceholder: "Additional notes...",
    createButton: "Create Invoice",
    creating: "Creating...",
    send: "Send",
    view: "View",
    delete: "Delete",
    invoiceSent: "Invoice sent to customer!",
    invoiceCreated: "Invoice created!",
    invoiceDeleted: "Invoice deleted",
    noInvoices: "No invoices",
    noInvoicesSubtitle: "Create your first invoice with integrated payment link",
    createFirst: "Create first invoice",
    status: {
      draft: "Draft",
      sent: "Sent",
      paid: "Paid",
      expired: "Expired",
      cancelled: "Cancelled",
      refunded: "Refunded"
    },
    from: "From",
    to: "To",
    invoiceNumber: "Invoice Number"
  },

  // Transactions
  transactions: {
    title: "Transactions",
    subtitle: "View and manage all your transactions",
    exportCSV: "Export CSV",
    id: "ID",
    date: "Date",
    amount: "Amount",
    commission: "Commission",
    netAmount: "Net",
    gateway: "Gateway",
    status: "Status",
    noTransactions: "No transactions",
    noTransactionsSubtitle: "Transactions will appear here when you receive payments",
    status_pending: "Pending",
    status_completed: "Completed",
    status_failed: "Failed",
    status_refunded: "Refunded",
    status_initialized: "Initialized",
    quickFilters: {
      today: "Today",
      week: "Week",
      month: "Month"
    },
    removeDuplicates: "Remove Duplicates",
    removing: "Removing...",
    markAsCompleted: "Mark as Completed",
    updating: "Updating...",
    adminAction: "Admin Action"
  },

  // API Keys
  apiKeys: {
    title: "API Keys",
    subtitle: "Manage your API keys for integration",
    createNew: "New API Key",
    createTitle: "Create New API Key",
    keyName: "Name",
    keyNamePlaceholder: "E.g.: Production, Test",
    ipWhitelist: "IP Whitelist (optional)",
    ipWhitelistPlaceholder: "192.168.1.1, 10.0.0.1",
    ipWhitelistHint: "Separate IPs with commas",
    createButton: "Create API Key",
    creating: "Creating...",
    keyCreated: "API Key created!",
    keyDeleted: "API Key deleted",
    saveSecretKey: "Save the Secret Key",
    secretKeyWarning: "It will not be visible after closing this window.",
    publicKey: "Public Key",
    secretKey: "Secret Key",
    keySaved: "I saved the keys",
    noKeys: "No API Keys",
    noKeysSubtitle: "Create an API key to integrate payments into your applications",
    createFirst: "Create first API Key",
    created: "Created",
    lastUsed: "Last used",
    documentation: "API Documentation",
    baseEndpoint: "Base Endpoint",
    authentication: "Authentication",
    authHint: "Add the Authorization header to your requests:"
  },

  // Branding
  branding: {
    title: "Branding",
    subtitle: "Customize the appearance of your payment links and invoices",
    businessInfo: "Business Information",
    displayName: "Display Name",
    legalName: "Legal Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    vatNumber: "VAT Number",
    visualCustomization: "Visual Customization",
    logoUrl: "Logo URL",
    logoHint: "PNG, JPG or SVG. Recommended size: 200x200px",
    primaryColor: "Primary Color",
    buttonColor: "Button Color",
    footerText: "Footer Text",
    saveChanges: "Save Changes",
    saving: "Saving...",
    saved: "Branding updated!",
    preview: "Preview",
    amountToPay: "Amount to pay",
    payNow: "Pay Now",
    securePayment: "Secure Payment"
  },

  // Withdrawals
  withdrawals: {
    title: "Withdrawals",
    subtitle: "Manage your withdrawal requests",
    requestNew: "Request Withdrawal",
    requestTitle: "Request Withdrawal",
    availableBalance: "Available balance",
    availableBalanceIn: "Available balance in",
    amount: "Amount",
    paymentMethod: "Payment Method",
    bankTransfer: "Bank Transfer",
    crypto: "Cryptocurrency",
    paypal: "PayPal",
    binance: "Binance",
    westernUnion: "Western Union",
    cash: "Cash",
    iban: "IBAN",
    accountHolder: "Account Holder",
    paypalEmail: "PayPal Email",
    submitRequest: "Submit Request",
    submitting: "Submitting...",
    requestSubmitted: "Withdrawal request submitted!",
    insufficientBalance: "Insufficient balance",
    noWithdrawals: "No withdrawals",
    noWithdrawalsSubtitle: "Your withdrawal requests will appear here",
    requested: "Requested",
    availableForWithdrawal: "Available for Withdrawal",
    withdrawalDetails: "Withdrawal Details",
    withdrawalId: "Withdrawal ID",
    commission: "Commission",
    netAmount: "Net Amount",
    selectNetwork: "Select Network",
    walletAddress: "Wallet Address",
    binanceId: "Binance ID",
    recipientName: "Recipient Name",
    city: "City",
    phone: "Phone",
    notes: "Notes",
    country: "Country",
    status: {
      pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
      paid: "Paid"
    }
  },

  // Admin Dashboard
  admin: {
    totalVolume: "Total Volume",
    platformRevenue: "Platform Revenue",
    activeMerchants: "Active Merchants",
    totalAgents: "Agents",
    pendingWithdrawals: "pending withdrawal requests",
    manage: "Manage",
    recentTransactions: "Recent Transactions",
    noTransactions: "No transactions",
    merchantManagement: "Merchant Management",
    agentManagement: "Agent Management",
    withdrawalRequests: "Withdrawal Requests",
    business: "Business",
    commissionRate: "Commission",
    agent: "Agent",
    noAgent: "None",
    createAgent: "New Agent",
    createAgentTitle: "Create New Agent",
    agentCreated: "Agent created!",
    noAgents: "No agents",
    registrationDate: "Registration Date",
    approve: "Approve",
    reject: "Reject",
    markPaid: "Mark Paid",
    withdrawalApproved: "Withdrawal approved",
    withdrawalRejected: "Withdrawal rejected",
    withdrawalPaid: "Withdrawal paid",
    noRequests: "No requests",
    merchantUpdated: "Merchant updated"
  },

  // Agent Dashboard
  agent: {
    commissionsEarned: "Commissions Earned",
    commissionsAvailable: "Commissions Available",
    networkVolume: "Network Volume",
    activeMerchants: "Active Merchants",
    merchantNetwork: "Merchant Network",
    noMerchants: "No merchants in your network",
    recentNetworkTransactions: "Recent Network Transactions",
    noTransactions: "No transactions"
  },

  // Public Payment Page
  payment: {
    amountToPay: "Amount to pay",
    chooseMethod: "Choose a payment method:",
    cardPayment: "Credit/Debit Card",
    enterAmount: "Enter amount",
    payWithPaypal: "Pay with PayPal",
    processing: "Processing...",
    securePayment: "Secure payment with PayPal",
    paymentCompleted: "Payment Completed!",
    thankYou: "Thank you for your payment. You will receive a confirmation by email.",
    paymentFailed: "Payment Failed",
    errorOccurred: "An error occurred during payment. Please try again.",
    retry: "Retry",
    linkExpired: "Link Expired",
    linkExpiredMessage: "This payment link has expired.",
    alreadyPaid: "Already Paid",
    alreadyPaidMessage: "This payment link has already been used.",
    invalidLink: "Invalid Link",
    invalidLinkMessage: "The payment link does not exist or has expired."
  },

  // Public Invoice Page
  invoice: {
    invoice: "Invoice",
    from: "From",
    to: "To",
    dueDate: "Due Date",
    description: "Description",
    qty: "Qty",
    price: "Price",
    total: "Total",
    subtotal: "Subtotal",
    tax: "Tax",
    discount: "Discount",
    notes: "Notes",
    pay: "Pay",
    invoicePaid: "Invoice Paid",
    paidOn: "Paid on",
    toPay: "To pay"
  },

  // Statuses
  statuses: {
    pending: "Pending",
    active: "Active",
    suspended: "Suspended",
    approved: "Approved",
    rejected: "Rejected",
    paid: "Paid",
    completed: "Completed",
    failed: "Failed",
    refunded: "Refunded",
    draft: "Draft",
    sent: "Sent",
    expired: "Expired",
    cancelled: "Cancelled"
  },

  // Errors
  errors: {
    generic: "An error occurred",
    networkError: "Network error",
    unauthorized: "Unauthorized",
    forbidden: "Access denied",
    notFound: "Not found",
    serverError: "Server error",
    validationError: "Validation error",
    requiredField: "Required field",
    invalidEmail: "Invalid email",
    invalidAmount: "Invalid amount",
    minLength: "Minimum length: {min} characters",
    maxLength: "Maximum length: {max} characters"
  },

  // Success messages
  success: {
    saved: "Saved successfully",
    created: "Created successfully",
    updated: "Updated successfully",
    deleted: "Deleted successfully",
    copied: "Copied!"
  },

  // Crypto Payout
  cryptoPayout: {
    title: "Crypto Payout",
    subtitle: "USDC/USDT/USDA Payouts on Polygon",
    merchantSubtitle: "Receive your earnings in USDC/USDT/USDA on Polygon",
    systemActive: "Active",
    systemInactive: "Inactive",
    refresh: "Refresh",
    // Tabs
    tabs: {
      overview: "Home",
      settings: "Settings",
      merchants: "Merchants",
      queue: "Queue",
      history: "History"
    },
    // Overview
    walletBalance: "Wallet Balance",
    notConfigured: "Not configured",
    belowThreshold: "Below threshold",
    completed: "Completed",
    inQueue: "In Queue",
    merchants: "Merchants",
    outOf: "out of",
    realTimeRates: "Real-Time Rates",
    loading: "Loading...",
    recentPayouts: "Recent Payouts",
    noPayout: "No payouts",
    // Settings
    settings: "Settings",
    systemEnabled: "System Active",
    automaticPayouts: "Automatic payouts",
    adminWallet: "Admin Wallet (Polygon)",
    walletDescription: "Wallet to send USDC/USDT/USDA to merchants.",
    privateKey: "Private Key (AES-256)",
    configured: "Configured",
    privateKeyWarning: "AES-256 encrypted. Use a dedicated wallet.",
    alertThreshold: "Alert Threshold ($)",
    alertDescription: "Telegram alert when balance < threshold.",
    telegramAlerts: "Telegram Alerts",
    payoutNotifications: "Payout notifications",
    saveSettings: "Save Settings",
    settingsSaved: "Settings saved",
    // Merchants
    merchantsTitle: "Merchant / Agent",
    wallet: "Wallet",
    token: "Token",
    frequency: "Freq.",
    payoutMode: "Payout mode",
    automaticMode: "Automatic",
    manualMode: "Manual approval",
    payoutModeHint: "This setting applies to new payout intents only.",
    roxpayManualApprovals: "RoxPay manual approvals",
    roxpayManualApprovalDescription: "Only RoxPay payouts configured for manual mode require approval before dispatch.",
    status: "Status",
    action: "Action",
    on: "ON",
    off: "OFF",
    config: "Config",
    configCryptoPayout: "Config Crypto Payout",
    polygonWallet: "Polygon Wallet",
    optional: "Optional",
    walletOptionalHint: "If not provided, the merchant can configure it from their dashboard",
    enable: "Enable",
    cancel: "Cancel",
    merchantEnabled: "Merchant enabled for crypto payout",
    merchantDisabled: "Merchant disabled",
    // Frequency
    everyTransaction: "Every TX",
    daily: "Daily",
    weekly: "Weekly",
    biweekly: "Every 15 days",
    monthly: "Monthly",
    // Queue
    queueTitle: "Payouts in Queue",
    retryAll: "Retry queued payouts",
    retryNoEligible: "No queued payouts are eligible for retry",
    retryNoCompleted: "No payout was confirmed; verify its state before retrying",
    retryAlreadyClaimed: "Another worker already claimed the payout; no new retry was started",
    retrySkipped: "Skipped",
    pendingReview: "Needs review",
    pendingNeedsReview: "Pending entries are not resent automatically; verify payout history and on-chain state to avoid duplicates",
    fiatAmount: "Fiat Amount",
    cryptoAmount: "Crypto Amount",
    rate: "Rate",
    date: "Date",
    waiting: "Waiting",
    noQueuePayouts: "No payouts in queue",
    retryCompleted: "Completed",
    retryFailed: "Failed",
    // History
    historyTitle: "Payout History",
    fiat: "Fiat",
    crypto: "Crypto",
    txHash: "TX Hash",
    completedStatus: "Completed",
    failedStatus: "Failed",
    noHistoryPayouts: "No completed payouts",
    // Merchant Settings Page
    merchantPage: {
      title: "Crypto Payout",
      subtitle: "Receive your earnings in USDC/USDT/USDA on Polygon",
      active: "Active",
      notConfigured: "Not configured",
      systemNotActive: "System not yet active",
      systemNotActiveDesc: "The crypto payout system has not yet been activated by the administrator. You can configure your wallet, but payouts will not be made until activation.",
      notAvailable: "Crypto Payout Not Available",
      notAvailableDesc: "This feature is not yet enabled for your account. Contact the administrator to request activation of cryptocurrency payouts.",
      liveRates: "Live Exchange Rates",
      source: "Source",
      updated: "Updated",
      example: "Example",
      configuration: "Configuration",
      yourWallet: "Your Polygon Wallet",
      walletPlaceholder: "Enter your Polygon wallet address (MetaMask, Trust Wallet, etc.)",
      preferredToken: "Preferred Token",
      payoutFrequency: "Payout Frequency",
      setByAdmin: "Set by Admin",
      frequencyDesc: {
        everyTransaction: "You will receive a crypto payout after each confirmed payment.",
        daily: "Payouts are accumulated and sent once a day.",
        weekly: "Payouts are accumulated and sent every Monday.",
        monthly: "Payouts are accumulated and sent on the 1st of the month."
      },
      saveConfig: "Save Wallet",
      configSaved: "Wallet saved! You will receive payouts in crypto.",
      howItWorks: "How It Works",
      step1: "Customer pays",
      step1Desc: "The customer pays with card, PayPal, etc.",
      step2: "Commission deducted",
      step2Desc: "The platform commission is withheld",
      step3: "Automatic conversion",
      step3Desc: "The amount is converted to {token} at the current rate",
      step4: "Payout on Polygon",
      step4Desc: "{token} sent to your wallet",
      activeConfig: "Active configuration",
      payoutsSentTo: "Payouts will be sent to",
      payoutHistory: "Payout History",
      noPayoutsYet: "No payouts yet",
      payoutsWillAppear: "Payouts will appear here after configuration"
    },
    // Validation
    enterWalletAddress: "Enter a Polygon wallet address",
    invalidWalletAddress: "Invalid wallet address. Must start with 0x and be 42 characters",
    errorLoading: "Error loading data",
    errorSaving: "Error saving",
    copied: "Copied!",
    error: "Error"
  },

  subscription: {
    notFound: "Plan not found", processing: "Subscription processing...", emailRequired: "Enter your email", invalidEmail: "Invalid email",
    approvalUrlMissing: "Error: approval URL not found", createError: "Error creating subscription", activated: "Subscription Activated!",
    thankYou: "Thank you for subscribing. You will receive a confirmation by email.", backToSite: "Back to Site", closeWindow: "Close Window",
    activationPending: "Your subscription is being activated.", cancelled: "Subscription Cancelled", cancelledMessage: "You cancelled the subscription process. You can try again anytime.",
    back: "Go Back", offeredBy: "Offered by", securePayment: "Secure payment with Hola Pay", easyCancellation: "Easy cancellation at any time",
    automaticCharge: "Automatic charge", nameOptional: "Name (optional)", processingPayment: "Processing payment...", subscribeNow: "Subscribe Now",
    securePaymentVia: "Secure payment via Hola Pay", poweredBy: "Powered by", intervals: { weekly: "week", monthly: "month", quarterly: "quarter", yearly: "year" }
  },
  terminals: {
    comingSoon: "Coming soon", mainFeatures: "Main Features", mainFeaturesDesc: "Everything you need to accept payments professionally",
    forEverySector: "Perfect for every sector", forEverySectorDesc: "Tailored solutions for your business", availableSoon: "Available soon",
    beFirst: "Want to be among the first?", ctaDescription: "Leave your contact and we will notify you as soon as terminals are available. The first 50 merchants will receive",
    exclusiveTerms: "exclusive terms", notifyMe: "Notify me", questions: "Have questions?", questionsDesc: "Our sales team is available to answer all your questions about POS terminals."
  },
  monitoring: {
    title: "System Monitoring", loading: "Loading metrics...", unavailable: "Snapshot unavailable", refreshNow: "Refresh now",
    historyLoadError: "Error loading alert history", testSentVia: "Test alert sent via", noChannels: "no active channels",
    testSendError: "Error sending test", backendUptime: "Backend uptime", activeThreads: "Active threads",
    payoutQueue: "Payout queue", failedHour: "Failed 1h", activeLocks: "Active locks", transactionsToday: "TX today",
    latestAlerts: "Latest alerts", noAlerts: "No alerts in history"
  },
  // Donations
  donate: {
    badge: "Donation",
    causeNotFound: "Cause not found",
    causeNotFoundDesc: "This donation cause does not exist or is no longer active.",
    goal: "Goal",
    reached: "reached",
    donors: "donors",
    selectAmount: "Select an amount",
    customAmount: "Enter custom amount",
    anonymous: "Donate anonymously",
    yourName: "Your name",
    namePlaceholder: "John Doe",
    yourEmail: "Your email",
    emailPlaceholder: "john@example.com",
    emailRequired: "Email required for non-anonymous donations",
    message: "Message (optional)",
    messagePlaceholder: "Leave a message of support...",
    donateNow: "Donate Now",
    processing: "Processing...",
    secure: "Secure and protected payment",
    poweredBy: "Powered by",
    error: "Error during donation. Please try again.",
    success: "Thank you for your donation!",
    // Admin
    causes: "Donation Causes",
    newCause: "New Cause",
    editCause: "Edit Cause",
    causeTitle: "Cause Title",
    causeDescription: "Description",
    goalAmount: "Fundraising Goal",
    suggestedAmounts: "Suggested Amounts",
    minAmount: "Minimum Amount",
    maxAmount: "Maximum Amount",
    assignToMerchant: "Assign to Merchant",
    selectMerchant: "Select Merchant",
    notAssigned: "Not assigned",
    totalRaised: "Total Raised",
    donationCount: "Donation Count"
  }
};

Object.assign(en, {
  apiKeysUi: {
    confirmDelete: "Are you sure you want to delete this API key?",
    quickGuide: "Quick integration guide", exampleRequest: "Example request",
    copyCurlExample: "Copy cURL example", fullDocumentation: "Full documentation"
  },
  transactionsUi: {
    removeDuplicatesConfirm: "Remove duplicate transactions? Only the oldest transaction for each ID will be kept.",
    statusPartiallyRefunded: "Partially refunded", defaultStatusSaved: "Default status saved",
    defaultStatusError: "Could not save the default status", saveDefaultHint: "Save the current status as the default for future visits",
    defaultLabel: "Default", setDefaultLabel: "Set as default", statusReconciled: "Reconciled (polling)",
    type: "Type", allTypes: "All types", allStatuses: "All statuses", allGateways: "All gateways",
    allMerchants: "All merchants", selectDate: "Select date", dateFrom: "From", dateTo: "To",
    clearFilters: "Clear filters", noFilterMatches: "No transactions match the selected filters",
    loadMoreTransactions: "Load {count} more transactions", visibleOfTotal: "({current} of {total})",
    loadNextPage: "Load next page", refundDate: "Refund date", resultsCount: "{count} transactions found"
  },
  cryptoPayoutUi: {
    selectMerchant: "Select a merchant first", selectAgent: "Select an agent first",
    agentEnabled: "Agent enabled for crypto payouts", agentDisabled: "Agent disabled", agent: "Agent",
    agentsTitle: "Agent crypto payouts", agentsDescription: "Configure automatic crypto payouts for agents",
    noAgentsFound: "No agents found", configureAgent: "Configure agent crypto payouts",
    chooseMerchant: "Choose a merchant", queueSelected: "Queue selected ({count})",
    noMissedInstantPayouts: "No unprocessed instant payouts were found in the last 48 hours",
    noEligibleMissedPayouts: "No eligible missed payouts found",
    noPayoutsQueued: "No payout was queued; its status may have changed",
    totalOnChainPayouts: "Total on-chain payouts", totalOnChainSummary: "Total on-chain payouts (USDC + USDT + USDA)",
    confirmRetryQueuedPayouts: "Retry {count} queued payout(s)? This can send real crypto transfers.",
    confirmRecoverStaleQueue: "This only requeues stale items with proof that no broadcast was authorized. It sends nothing now, but eligible items may later be picked up by automatic retry. Continue?",
    recoveryComplete: "Queue recovery complete: {requeued} safely requeued · {review} require review · {skipped} unchanged. This action did not send a transfer; eligible items may be retried automatically.",
    noStaleItemsRecovered: "No stale queue items could be safely recovered.",
    queuePayoutsAutoEnabled: "Queue these payouts? Automatic processing is enabled, so real crypto transfers may start without another review.",
    queuePayoutsAutoDisabled: "Queue these payouts? They will remain queued until automatic processing is enabled.",
    skippedCount: " · skipped: {count}", payoutsQueued: "Payouts queued: {count}{skipped}",
    findMissedPayouts: "Find missed payouts", recoverStale: "Recover stale items",
    processing: "Processing", requiresReview: "Requires review",
    queueRecoveryWarning: "{processing} processing, {review} requiring review. These are excluded from retry; reconcile them before taking action.",
    roxpayActiveIntents: "Active RoxPay payout intents",
    roxpayIntentsDescription: "RoxPay outbox status, refreshed automatically every 30 seconds. Intents already handed to the payout queue appear in the table below.",
    roxpayIntentsUnavailable: "RoxPay intent status is unavailable. Update the backend or try again.",
    noRoxpayActiveIntents: "No active RoxPay intents in the outbox.",
    roxpayIntentsTruncated: "Showing up to 200 active intents.",
    refreshRoxpayIntents: "Refresh RoxPay intent status",
    recipient: "Recipient", transaction: "Transaction", mode: "Mode",
    attemptsAndNextStep: "Attempts and next deadline",
    outboxProcessing: "Processing", outboxPending: "Pending",
    outboxAwaitingApproval: "Awaiting approval", outboxBlocked: "Blocked",
    outboxAutomatic: "Automatic", outboxManual: "Manual",
    attempts: "Attempts", leaseUntil: "Lease until", nextAttempt: "Next attempt",
    createdAt: "Created", lastError: "Last error",
    missedInstantPayoutsTitle: "Missed instant payouts",
    automaticProcessingEnabled: "Automatic processing is on. Queued payouts may trigger real crypto transfers.",
    automaticProcessingDisabled: "Automatic processing is off. Queued payouts will wait until it is enabled.",
    disable: "Disable"
  },
  terminalDetails: {
    featureContactlessTitle: "Contactless & NFC",
    featureContactlessDescription: "Accept tap-to-pay cards, smartphones, and smartwatches. Instant transactions in under 2 seconds.",
    featureChipPinTitle: "Chip & PIN",
    featureChipPinDescription: "Full support for EMV chip and PIN cards. Maximum security for high-value transactions.",
    featurePreauthorizationTitle: "Pre-authorizations",
    featurePreauthorizationDescription: "Ideal for hotels and car rentals. Place a hold on the customer's card and charge only the final amount.",
    featureReceiptsTitle: "Digital receipts",
    featureReceiptsDescription: "Send receipts by SMS, email, or WhatsApp. Go paperless with professional, traceable receipts.",
    featureTipsTitle: "Digital tips",
    featureTipsDescription: "Suggest tip amounts on the display. Increase staff tips by up to 30%.",
    featureConnectivityTitle: "4G/Wi-Fi connectivity",
    featureConnectivityDescription: "Stay connected with dual connectivity. Ideal for deliveries, events, and markets.",
    advancedHotelPreauthorization: "Hotel pre-authorizations (early check-in)",
    advancedCarRentalPreauthorization: "Car rental pre-authorizations",
    advancedRestaurantOrders: "Table orders and payments for restaurants",
    advancedRetailInventory: "Integrated retail inventory",
    advancedOperatorReports: "Multiple operators with individual reports",
    advancedMultiCurrency: "Multi-currency acceptance (EUR, USD, GBP)",
    advancedLiveNotifications: "Real-time notifications for every transaction",
    advancedWhatsappReceipts: "Automatic WhatsApp receipts",
    advancedBrandedEmailReceipts: "Branded email receipts with your logo",
    advancedDailyReports: "Automatic daily reports",
    advancedPciCertification: "PCI-DSS Level 1 certification",
    advancedSupport: "Dedicated 24/7 customer support",
    sectorRestaurants: "Restaurants & bars", sectorHotels: "Hotels & B&Bs",
    sectorRetail: "Retail & shops", sectorFoodTrucks: "Food trucks",
    sectorCarRental: "Car rentals", sectorProfessionals: "Professionals",
    advancedBadge: "Advanced features", advancedTitle: "Everything included, no surprises",
    advancedDescription: "Enterprise features available for every type of business",
    heroTitle: "POS terminals",
    heroSubtitle: "Accept in-person payments with our next-generation terminals. Manage online and in-store sales from one dashboard.",
    contactlessBadge: "Contactless", realtimeBadge: "Real-time",
    notifyWhatsappMessage: "Hi! I would like to be notified when Alpha Bit Pay POS terminals are available.",
    notifyButton: "Notify me when available", transactionTime: "Transaction time",
    uptimeGuarantee: "Guaranteed uptime", dedicatedSupport: "Dedicated support", monthlyFee: "Monthly fee",
    contactWhatsappMessage: "Hi! I'd like information about Hola Pay POS terminals."
  }
});
Object.assign(en.monitoring, { errorRate: "Error rate" });

export default en;
